import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { LoadingSpinner, Button } from '../components/Common';
import { DynamicIcon } from '../components/icons';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { motion, AnimatePresence } from 'framer-motion';
import { notify } from '@/components/core';

// ── Inline confirm dialog (no browser window.confirm) ─────────────────────────
const ConfirmDialog = ({ open, onConfirm, onCancel }) => (
    <AnimatePresence>
        {open && (
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={onCancel}
                />

                {/* Panel */}
                <motion.div
                    className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm p-6 flex flex-col gap-4"
                    initial={{ scale: 0.92, opacity: 0, y: 16 }}
                    animate={{ scale: 1,    opacity: 1, y: 0  }}
                    exit={{    scale: 0.92, opacity: 0, y: 16 }}
                    transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                >
                    {/* Icon + heading */}
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 shrink-0">
                            <DynamicIcon name="ShieldOff" className="w-5 h-5" />
                        </div>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                            Terminate Session?
                        </h2>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        This will immediately revoke access for that device. The user will be signed out and cannot resume the session.
                    </p>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-1">
                        <Button variant="secondary" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                        <button
                            onClick={onConfirm}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors shadow-sm"
                        >
                            <DynamicIcon name="Trash2" className="w-3.5 h-3.5" />
                            Revoke Access
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        )}
    </AnimatePresence>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const SecuritySettings = ({ token }) => {
    const [sessions, setSessions]           = useState([]);
    const [isLoading, setIsLoading]         = useState(true);
    const [confirmId, setConfirmId]         = useState(null); // session_id awaiting confirmation
    const { formatDate }                    = useTimezoneFormatter();


    const fetchSessions = async () => {
        try {
            const res = await apiFetch('/auth/sessions', token);
            setSessions(res.sessions || []);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
            notify.error('Could not load active sessions');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchSessions(); }, [token]);

    // Step 1 — open dialog
    const handleRevokeClick = (sessionId) => setConfirmId(sessionId);

    // Step 2a — confirmed
    const handleConfirm = async () => {
        const sessionId = confirmId;
        setConfirmId(null);
        try {
            await apiFetch(`/auth/sessions/${sessionId}`, token, { method: 'DELETE' });
            notify.success('Session terminated successfully');
            fetchSessions();
        } catch (err) {
            notify.error(err.message);
        }
    };

    // Step 2b — cancelled
    const handleCancel = () => setConfirmId(null);

    if (isLoading) return <LoadingSpinner />;

    const currentSid = sessionStorage.getItem('auth_sid');

    return (
        <>
            {/* Confirm dialog — rendered at root level so it overlays everything */}
            <ConfirmDialog
                open={!!confirmId}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

            <div className="space-y-6">
                {/* Sessions card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            <DynamicIcon name="Shield" className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Active Sessions</h3>
                            <p className="text-xs text-gray-500">Manage your active login sessions across devices.</p>
                        </div>
                    </div>

                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {sessions.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">No active sessions found.</div>
                        ) : (
                            sessions.map((session) => (
                                <div
                                    key={session.session_id}
                                    className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-500">
                                            <DynamicIcon
                                                name={session.device_info?.toLowerCase().includes('mobile') ? 'Smartphone' : 'Monitor'}
                                                className="w-5 h-5"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                                    {session.device_info || 'Unknown Device'}
                                                </span>
                                                {session.session_id === currentSid && (
                                                    <span className="px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-bold uppercase">
                                                        Current Session
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                                    <DynamicIcon name="MapPin" className="w-3.5 h-3.5" />
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                                        {[session.city, session.country_name].filter(Boolean).join(', ') || session.ip_address || 'Unknown Location'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                    <DynamicIcon name="Clock" className="w-3.5 h-3.5" />
                                                    <span>Started {formatDate(session.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 self-end md:self-center">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-100 dark:border-red-900/30"
                                            onClick={() => handleRevokeClick(session.session_id)}
                                            disabled={session.session_id === currentSid}
                                        >
                                            Revoke Access
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Security tip */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 flex gap-3">
                    <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                        <DynamicIcon name="Info" className="w-5 h-5" />
                    </div>
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                        <p className="font-medium mb-1">Security Recommendation</p>
                        <p>If you see a device or location you don't recognize, revoke the session immediately and change your password.</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SecuritySettings;
